import { useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Network, Plus, X } from 'lucide-react'
import { TemplateResponseData } from '@/services/inference.gen'
import { cn } from '@/lib/utils'
import type { DashboardNewServerFormData } from './index'

type PortManagerProps = {
  form: UseFormReturn<DashboardNewServerFormData>
  selectedTemplate: TemplateResponseData
}

const MAX_PORTS = 3

export const PortManager = ({ form, selectedTemplate }: PortManagerProps) => {
  const [newPort, setNewPort] = useState('')
  const [isInputDisabled, setIsInputDisabled] = useState(false)
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false)

  const handleAddPort = () => {
    if (!newPort) {
      form.setError('httpPorts', {
        type: 'manual',
        message: 'Invalid port number.',
      })
      return
    }

    if (
      selectedTemplate.service_ports
        .map((p) => p.port)
        .includes(Number(newPort))
    ) {
      form.setError('httpPorts', {
        type: 'manual',
        message: 'This port is already in use by the template.',
      })
      return
    }

    const currentPorts = form.getValues('httpPorts')
    const portNumber = parseInt(newPort, 10)

    if (isNaN(portNumber) || portNumber < 1000 || portNumber > 99999) {
      form.setError('httpPorts', {
        type: 'manual',
        message: 'Port must be a number between 1000 and 99999.',
      })
      return
    }

    if (currentPorts.includes(newPort)) {
      form.setError('httpPorts', {
        type: 'manual',
        message: 'This port has already been added.',
      })
      return
    }

    if (currentPorts.length >= MAX_PORTS) {
      form.setError('httpPorts', {
        type: 'manual',
        message: `You can only add up to ${MAX_PORTS} ports.`,
      })
      return
    }

    form.setValue('httpPorts', [...currentPorts, newPort])
    setNewPort('')
    setIsInputDisabled(currentPorts.length + 1 === MAX_PORTS)
    setIsAddButtonDisabled(currentPorts.length + 1 === MAX_PORTS)
    form.clearErrors('httpPorts')
  }

  const handlePortInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (/^\d{0,5}$/.test(value)) {
      setNewPort(value)
      setIsAddButtonDisabled(!value)
    }
  }

  const handleRemovePort = (port: string) => {
    const updatedPorts = form
      .getValues('httpPorts')
      .filter((p: string) => p !== port)
    form.setValue('httpPorts', updatedPorts)
    if (updatedPorts.length < MAX_PORTS) {
      setIsInputDisabled(false)
      setIsAddButtonDisabled(false)
    }
  }

  return (
    <div
      className={cn(
        'space-y-2 rounded-lg p-3 min-h-[90px] border transition-all col-span-full',
        'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
      )}
      data-testid="http-ports-card"
    >
      <div className="flex items-center gap-2 border-b border-border/40 pb-2">
        <div className="rounded-md p-1 bg-muted/90">
          <Network className="h-5 w-5" />
        </div>
        <span className="text-sm font-semibold">HTTP Ports</span>
      </div>

      <FormField
        control={form.control}
        name="httpPorts"
        render={() => (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <FormLabel className="text-xs text-muted-foreground m-0">
                Template
              </FormLabel>
              <div className="flex flex-wrap justify-end gap-1">
                {selectedTemplate.service_ports &&
                selectedTemplate.service_ports.length > 0 ? (
                  selectedTemplate.service_ports.map((port) => (
                    <span
                      key={port.port}
                      className="text-xs font-medium px-2 py-0.5 bg-secondary/20 border border-secondary/20 rounded-md"
                    >
                      {port.port}
                    </span>
                  ))
                ) : (
                  <span className="text-xs px-2 py-0.5 bg-background border border-border/50 rounded-md">
                    None
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Custom</span>
              <div className="flex flex-wrap justify-end gap-1">
                {form.watch('httpPorts').length > 0 ? (
                  form.watch('httpPorts').map((port: string) => (
                    <div
                      key={port}
                      className="group flex items-center text-xs font-medium px-2 py-0.5 bg-primary/10 border border-primary/20 rounded-md"
                    >
                      {port}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-3.5 w-3.5 ml-1 opacity-70 group-hover:opacity-100"
                        onClick={() => handleRemovePort(port)}
                      >
                        <X className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <span className="text-xs px-2 py-0.5 bg-background border border-border/50 rounded-md">
                    None
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 mt-1">
              <FormControl className="flex-1">
                <Input
                  value={newPort}
                  onChange={handlePortInputChange}
                  placeholder="Add port..."
                  disabled={isInputDisabled}
                  data-testid="http-port-input"
                  className="h-7 text-xs py-0"
                />
              </FormControl>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleAddPort}
                disabled={isAddButtonDisabled || !newPort}
                data-testid="add-port-button"
                className="h-7 w-7"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
              <FormMessage />
            </div>
          </div>
        )}
      />
    </div>
  )
}
